import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";
import { Button } from "primereact/button";
import PreLoginContainer from "../../common/preLoginContainer";
import Link from "../../common/link";
import { RouteConstants } from "../../../constants";
import {
  verifyEmailRequest,
  resetState,
} from "../../../store/slices/registrationSlice";

/**
 * VerifyEmail Component
 *
 * A component for email verification functionality after user registration.
 * This component handles email verification using tokens sent via email.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.registration - Registration state from Redux
 * @param {Function} props.verifyEmail - Action to verify email
 * @param {Function} props.resetState - Action to reset registration state
 * @returns {JSX.Element} The email verification component
 */
const VerifyEmail = ({ registration, verifyEmail, resetState }) => {
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationAttempted, setVerificationAttempted] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(3);

  // Extract token from query parameters
  const token = searchParams.get("token");

  useEffect(() => {
    resetState();
  }, [resetState]);

  useEffect(() => {
    if (token && !verificationAttempted) {
      setVerificationAttempted(true);
      const params = searchParams.get("params");
      verifyEmail({ token, params });
    }
  }, [token, searchParams, verifyEmail, verificationAttempted]);

  // Auto-redirect after successful verification
  useEffect(() => {
    if (registration.emailVerificationSuccess) {
      const timer = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate(RouteConstants.APP_ROUTE_LOGIN);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [registration.emailVerificationSuccess, navigate]);

  const handleReturnToLogin = () => {
    navigate(RouteConstants.APP_ROUTE_LOGIN);
  };

  const getVerificationStatus = () => {
    if (registration.requesting && registration.action === "verify-email") {
      return "verifying";
    }

    if (registration.emailVerificationSuccess) {
      return "success";
    }

    if (registration.error && verificationAttempted) {
      return "error";
    }

    return "initial";
  };

  const renderVerificationContent = () => {
    const status = getVerificationStatus();

    switch (status) {
      case "verifying":
        return (
          <div className="text-center">
            <ProgressSpinner />
            <div className="mt-4">
              <h3 className="text-xl font-semibold mb-3">
                {intl.formatMessage({ id: "verifying_email" })}
              </h3>
              <p className="text-muted">
                {intl.formatMessage({ id: "verifying_email_message" })}
              </p>
            </div>
          </div>
        );

      case "success":
        return (
          <div className="text-center">
            <div className="mb-4">
              <i className="pi pi-check-circle text-green-500 text-6xl"></i>
            </div>
            <h3 className="text-xl font-semibold mb-3 text-green-600">
              {intl.formatMessage({ id: "email_verification_success" })}
            </h3>
            <p className="text-muted mb-4">
              {intl.formatMessage({ id: "email_verification_success_message" })}
            </p>
            <p className="text-muted mb-4">
              {intl.formatMessage(
                { id: "redirecting_to_login_countdown" },
                { seconds: redirectCountdown }
              )}
            </p>
            <Button
              label={intl.formatMessage({ id: "continue_to_login" })}
              icon="pi pi-arrow-right"
              className="p-button-success"
              onClick={handleReturnToLogin}
            />
          </div>
        );

      case "error":
        return (
          <div className="text-center">
            <div className="mb-4">
              <i className="pi pi-times-circle text-red-500 text-6xl"></i>
            </div>
            <h3 className="text-xl font-semibold mb-3 text-red-600">
              {intl.formatMessage({ id: "email_verification_failed" })}
            </h3>
            <p className="text-muted mb-4">
              {registration.error?.response
                ? intl.formatMessage(
                    { id: registration.error.response.toLowerCase() },
                    {},
                    intl.formatMessage({ id: "email_verification_error" })
                  )
                : intl.formatMessage({ id: "email_verification_error" })}
            </p>
            <div className="flex flex-column gap-2">
              <Button
                label={intl.formatMessage({ id: "try_again" })}
                icon="pi pi-refresh"
                className="p-button-secondary"
                onClick={() => {
                  setVerificationAttempted(false);
                  resetState();
                }}
              />
              <Button
                label={intl.formatMessage({ id: "back_to_login" })}
                icon="pi pi-arrow-left"
                className="p-button-text"
                onClick={handleReturnToLogin}
              />
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center">
            <div className="mb-4">
              <i className="pi pi-envelope text-blue-500 text-6xl"></i>
            </div>
            <h3 className="text-xl font-semibold mb-3">
              {intl.formatMessage({ id: "email_verification" })}
            </h3>
            <p className="text-muted mb-4">
              {intl.formatMessage({ id: "email_verification_initial_message" })}
            </p>
            <Link to={RouteConstants.APP_ROUTE_LOGIN}>
              {intl.formatMessage({ id: "back_to_login" })}
            </Link>
          </div>
        );
    }
  };

  return (
    <PreLoginContainer>
      <div className="verify-email-container">
        {renderVerificationContent()}
      </div>
    </PreLoginContainer>
  );
};

const mapStateToProps = (state) => ({
  registration: state.registration,
});

const mapDispatchToProps = (dispatch) => ({
  verifyEmail: (data) => dispatch(verifyEmailRequest(data)),
  resetState: () => dispatch(resetState()),
});

export default connect(mapStateToProps, mapDispatchToProps)(VerifyEmail);
